import * as Sentry from '@sentry/react';
import { getApiBridge } from '@packages/api';
import { recordAnonymousUiUsageEvent, recordUiUsageEvents } from '@packages/api/endpoints/ui-usage.js';
import { parseCampaignAttribution, uiArrivalOf, type TrackedUiEventName } from '@packages/types';
import { CLIENT_VERSION } from '../config';
import { createAnonymousUiUsageSender } from './ui-usage.anonymous';
import { createUiUsageChannel } from './ui-usage.channel';
import { configureUiUsage, type UiUsageSink } from './ui-usage.runtime';
import { isDesktopViewport } from '../viewport';

/**
 * Reports an event the wire schema refused: a log in development, one Sentry message per event
 * name in production (so a broken call site cannot flood Sentry). Carries the name and zod issue
 * paths only, never a payload value. Telemetry never surfaces to the user.
 */
export function createUiUsageRejectReporter(development: boolean): UiUsageSink['reject'] {
  const reported = new Set<TrackedUiEventName>();
  return (name, issuePaths) => {
    if (development) {
      console.error(`UI usage event ${name} was refused by the wire schema`, issuePaths);
      return;
    }
    if (reported.has(name)) return;
    reported.add(name);
    Sentry.captureMessage(`UI usage event refused: ${name}`, { level: 'warning', extra: { issuePaths } });
  };
}

/**
 * Installs the production runtime: the token-driven account channel (with its hide and
 * `pagehide` listeners), the immediate anonymous sender, the browser's viewport and build
 * version, and the arrival derived once from the URL this page loaded with. Called once per app boot, after
 * `initializeApi`, because the channel reads the session token from the API bridge.
 */
export function installUiUsage(): void {
  const channel = createUiUsageChannel({
    send: recordUiUsageEvents,
    readToken: () => getApiBridge().getToken(),
  });
  configureUiUsage({
    sink: {
      accept: channel.accept,
      acceptAnonymous: createAnonymousUiUsageSender({
        send: recordAnonymousUiUsageEvent,
        readToken: () => getApiBridge().getToken(),
      }),
      reject: createUiUsageRejectReporter(import.meta.env.DEV),
    },
    viewport: () => (isDesktopViewport() ? 'desktop' : 'phone'),
    clientVersion: CLIENT_VERSION,
    // The boot URL only: no storage read, so arrival is `none` after a full page load.
    arrival: uiArrivalOf(parseCampaignAttribution(window.location.search)),
    randomUUID: () => crypto.randomUUID(),
  });
}
